import { faAdd, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useEffect, useState } from 'react';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import KeywordTable from '../../components/muiLegacy/Tables/KeywordTable/KeywordTable';
import {
  KeywordTableColumn,
  KeywordTableColumnTypes,
} from '../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/models';
import { useGetDebouncedKeywords } from '../../components/muiLegacy/Tables/KeywordTable/logic/useGetDebouncedKeywords';
import { useKeywordTableState } from '../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import DataTableToolbarButton from '../../components/voxel/DataTableToolbar/DataTableToolbarButton';
import { VoxelPopover } from '../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelMenuItemContainer from '../../components/voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelPopoverMenuItem from '../../components/voxel/PopoverMenu/VoxelPopoverMenuItem';
import { ProjectTypeGq } from '../../graphql/projects/models/ProjectTypeGq';
import { languageList } from '../../graphql/projects/models/languages';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { FrontendUserEventGq } from '../../graphql/users/queries/useFrontendLogUserEventGq';
import { usersApiService } from '../../graphql/users/usersApiService';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import { useUserDefaults } from '../../logic/otherUtilities/useUserDefaults';
import VoxelVerticalDrawer from '../../components/voxel/Drawers/VoxelVerticalDrawer';
import NewProjectDialog from '../StartPage/components/NewProjectDialog/NewProjectDialog';
import { NewProjectFormState } from '../StartPage/components/NewProjectDialog/NewProjectFormState';
import StartPageAppBar from '../StartPage/components/StartPageAppBar';
import QuickSearchForm from './components/Forms/QuickSearchForm/QuickSearchForm';
import QuickSearchList from './components/QuickSearchList';
import { AddToExistingProjectDialog } from './components/AddToExistingProjectDialog';
import { MenuItem } from '../../components/voxel/Menu/VoxelMenu';
import VoxelText from '../../components/voxel/Text/VoxelText';
import VoxelToggleTabs from '../../components/voxel/Tabs/VoxelToggleTabs';

export interface QuickSearchPageProps {
  requestId?: number;
  projectId?: number;
  setRequest: (requestId: number, projectId: number) => void;
}

const menuItems: MenuItem[] = [
  { id: 'new', name: 'New search' },
  { id: 'list', name: 'Search list' },
];

const QuickSearchPage: React.FC<QuickSearchPageProps> = ({ requestId, projectId, setRequest }) => {
  // Log user event
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();

  useEffect(() => {
    logUserEventMutation({
      event: FrontendUserEventGq.VISIT_QUICK_SEARCH,
    });
  }, []);

  // Get user
  const { user, team, query, availableUsers } = usersApiService.queries.useGetCurrentUser();

  const [QSTab, setQSTab] = useState<string>(requestId && projectId ? 'list' : 'new');

  const basePollIntervall = 15000;
  const [pollInterval, setPollInterval] = useState(basePollIntervall);

  const [addProjectAccessOpen, setAddProjectAccessOpen] = useState(false);

  // Google ads blocked request
  const blockedQuery = projectsApiService.queries.googleAds.useGetGoogleAdsBlocked(
    { dummy: '' },
    { skip: QSTab !== 'new', pollInterval: pollInterval }
  );

  // Historic requests
  const searchListQuery = projectsApiService.queries.googleAds.useGetGoogleAdsRequests(
    {
      projectType: ProjectTypeGq.QUICK_SEARCH,
      page: 1,
      itemsPerPage: 100, // Only the 100 last searches.
    },
    {
      skip: QSTab !== 'list',
      pollInterval: pollInterval,
    }
  );

  const analyzedRequest = searchListQuery?.result?.analyzedRequests?.find(
    (rt) => rt.request.id === requestId
  );

  // Project data
  const projectQuery = projectsApiService.queries.useGetProject(
    { id: projectId },
    {
      skip: !projectId,
      pollInterval: pollInterval,
    }
  );

  // Keyword table data (for viewed request)
  const requestQuery = projectsApiService.queries.googleAds.useGetGoogleAdsRequest(
    {
      id: requestId,
      projectId: projectId,
    },
    {
      skip: !requestId || !projectId,
      pollInterval: pollInterval,
    }
  );

  // Keyword table state (for viewed request)
  const columns: KeywordTableColumn[] = [
    { type: KeywordTableColumnTypes.Keyword, order: 0 },
    { type: KeywordTableColumnTypes.Volume, order: 1 },
    { type: KeywordTableColumnTypes.VolumeTrend, order: 2 },
    { type: KeywordTableColumnTypes.CpcBidHigh, order: 3 },
    { type: KeywordTableColumnTypes.Competition, order: 4 },
  ];

  const keywordTableState = useKeywordTableState(
    columns,
    {
      requestId: requestId,
      projectId: projectId,
    },
    undefined,
    undefined
  );

  const keywordQueryOptions = {
    pollInterval: pollInterval,
    skip: !projectId || !keywordTableState.filterState.projectId,
  };

  const selectedKeywordStats = projectsApiService.queries.useGetSelectedKeywordsStats(
    {
      projectId: projectId,
      keywordQuery: {
        filtering: keywordTableState.backendFilterState,
        selecting: { ...keywordTableState.backendSelectionState, emptyIsAll: true },
      },
    },
    keywordQueryOptions
  );

  const {
    keywords,
    totalCount,
    totalVolume,
    page,
    query: keywordQuery,
  } = useGetDebouncedKeywords(keywordTableState, keywordQueryOptions);
  const googleAdsRequestAnalysis = requestQuery?.result;
  const loading =
    !googleAdsRequestAnalysis ||
    googleAdsRequestAnalysis.queuedCalls > 0 ||
    googleAdsRequestAnalysis.processingCalls > 0 ||
    googleAdsRequestAnalysis.suggestSeedsTotal > googleAdsRequestAnalysis.suggestSeedsCompleted ||
    googleAdsRequestAnalysis.suggestKeywordsBatching;

  let selectionActive = false;
  if (!!keywordTableState.backendSelectionState?.keywordIds.length) selectionActive = true;
  if (!!keywordTableState.backendSelectionState?.invertSelection) selectionActive = true;
  const selectedCount = selectionActive ? selectedKeywordStats.result.selectedCount : 0;

  // Adjust poll time based on loading states
  useEffect(() => {
    if (loading) setPollInterval(1000);
    else setPollInterval(basePollIntervall);
  }, [loading]);

  // Project conversion form values
  const [openCreateProjectDialog, setOpenCreateProjectDialog] = useState(false);
  const currentLocation = projectsApiService.queries.googleAds.useGetLocations({
    id: googleAdsRequestAnalysis?.request?.locationId,
  });
  const countryQuery = projectsApiService.queries.googleAds.useGetLocations({
    targetType: 'Country',
  });
  const conversionLocation = currentLocation?.result ? currentLocation?.result[0] : null;
  const defaults = useUserDefaults(user);
  let projectConversionValues: Partial<NewProjectFormState> = {
    language: googleAdsRequestAnalysis?.request?.languageId
      ? languageList?.find(
        (language) => language.id === googleAdsRequestAnalysis?.request?.languageId
      )
      : null,
    country:
      conversionLocation?.targetType !== 'City'
        ? conversionLocation
        : countryQuery?.result?.find((l) => l.countryCode === conversionLocation?.countryCode),
    city: conversionLocation?.targetType === 'City' ? conversionLocation : null,
    includeZeroVolume: !!googleAdsRequestAnalysis?.request?.minSearchVolume,
    currency: defaults?.currency,
  };

  useEffect(() => {
    const devPrefix = window.location.href.includes('localhost') ? 'Dev | ' : '';
    document.title = `${devPrefix}Quick search`;
  }, []);

  const isScrolling = useIsScrolling();

  // Render
  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <>
      {user && openCreateProjectDialog && (
        <NewProjectDialog
          keywordTableState={keywordTableState}
          openDialog={openCreateProjectDialog}
          setOpenDialog={setOpenCreateProjectDialog}
          startValues={projectConversionValues}
          qSProjectId={projectId}
          user={user}
        />
      )}
      {addProjectAccessOpen && (
        <AddToExistingProjectDialog
          keywordTableState={keywordTableState}
          open={addProjectAccessOpen}
          onClose={() => setAddProjectAccessOpen(false)}
          teamId={user.team.id}
          projectId={projectId}
          languageId={requestQuery?.result?.request?.languageId}
          locationId={requestQuery?.result?.request?.locationId}
          user={user}
        />
      )}
      <StartPageAppBar
        team={team}
        user={user}
        isScrolling={isScrolling}
        availableUsers={availableUsers}
      />
      <div className={'h-12'} /> {/* StartPageAppBar size */}
      <div className="flex flex-row h-full">
        <VoxelVerticalDrawer
          open={true}
          openWidthClass="w-80"
          openMinWidthClass="min-w-80"
          shadowClass="shadow-none"
          bgColorClass="bg-white"
          zIndexClass="z-20"
        >
          <div className="flex flex-col">
            <div className="pt-4 pb-2 flex flex-row items-center justify-between">
              <span className="pl-5">
                <VoxelToggleTabs
                  menuListItems={menuItems}
                  selectedId={QSTab}
                  setSelectedId={setQSTab}
                />
              </span>
            </div>
            {QSTab === 'new' && (
              <div className="overflow-y-auto max-h-[calc(100vh-48px-48px-8px)]">
                <QuickSearchForm
                  setRequestId={setRequest}
                  user={user}
                  selectedRequestId={requestId}
                  loading={loading}
                  blockedQueryResult={blockedQuery.result}
                />
              </div>
            )}
            {QSTab === 'list' && (
              <div className="flex flex-col overflow-y-auto max-h-[calc(100vh-48px-48px-8px)]">
                {searchListQuery.result.totalCount > 0 ? (
                  <QuickSearchList
                    setRequestId={setRequest}
                    requestId={requestId}
                    searchListData={searchListQuery.result.analyzedRequests}
                  />
                ) : (
                  <VoxelText
                    variant="form-caption"
                    paddingTop
                    italic
                    className="pl-4 p-2 py-2.5 ml-2"
                  >
                    No searches added yet
                  </VoxelText>
                )}
              </div>
            )}
          </div>
        </VoxelVerticalDrawer>

        {/* Keyword table */}
        <div className={`flex-grow h-full mx-auto px-4`}>
          <KeywordTable
            project={projectQuery?.result}
            keywordTableState={keywordTableState}
            totalCount={totalCount}
            totalVolume={totalVolume}
            selectedKeywordStats={selectedKeywordStats.result}
            data={keywords}
            changeVisibleColumns
            loading={loading}
            currentPage={page}
            includeExportMonths={!!googleAdsRequestAnalysis?.request?.includeMonths}
            reloadKeywords={keywordQuery.refetch}
            isScrolling={isScrolling}
            page="quicksearch"
            extraButtons={
              <>
                <VoxelPopover>
                  <VoxelPopoverTrigger enableActive>
                    <DataTableToolbarButton
                      iconProp={faAdd}
                      label="Add to project"
                      disabled={!projectQuery?.result?.progressKeywordCount || !(selectedCount > 0)}
                      effect={selectedCount > 0 ? 'primary' : undefined}
                      effectAnimate={false}
                    />
                  </VoxelPopoverTrigger>
                  <VoxelPopoverContent>
                    <VoxelMenuItemContainer size="md">
                      <VoxelPopoverMenuItem
                        onClick={() => setOpenCreateProjectDialog(true)}
                        title={'New project'}
                        icon={<FontAwesomeIcon icon={faAdd} />}
                      />
                      <VoxelPopoverMenuItem
                        onClick={() => setAddProjectAccessOpen(true)}
                        title={'Existing project'}
                        icon={<FontAwesomeIcon icon={faSearch} />}
                      />
                    </VoxelMenuItemContainer>
                  </VoxelPopoverContent>
                </VoxelPopover>
              </>
            }
          />
        </div>
      </div>
    </>
  );
};

export default QuickSearchPage;
